import React from 'react';
import { AiAssistantControls, AiAssistantSuggestedPrompts } from '@worktango/ai-assistant/react';
import { useLocation, Location } from 'react-router-dom';

export const SuggestedPrompts = (props: { controls: React.RefObject<AiAssistantControls> }) => {
  const setPrompt = React.useCallback(
    (prompt: string) => {
      props.controls?.current?.setPrompt?.(prompt);
    },
    [props.controls],
  );
  const location = useLocation();
  const suggestedPrompts = getSuggestedPrompts({ location });
  const allTotal = Object.values(suggestedPrompts).flat().length;

  if (allTotal === 0) {
    return null;
  }

  return (
    <AiAssistantSuggestedPrompts
      onSelect={(prompt) => setPrompt(prompt.fullPrompt ?? prompt.title)}
      suggestedPrompts={suggestedPrompts}
      includeGlobalPrompts={false}
    />
  );
};

type SuggestedPrompt = {
  title: string;
  description?: string;
  match?: (location: Location<any>) => boolean;
};

function getSuggestedPrompts({ location }: { location: Location<any> }): Record<string, SuggestedPrompt[]> {
  return {
    'Suggested prompts for this page': suggestedPagePrompts.filter((prompt) => prompt.match?.(location)),
    // We can probably remove this, as we are using this for the global default prompts
    'WorkTango Coach prompts': globalSuggestedPrompts,
  };
}

const globalSuggestedPrompts: SuggestedPrompt[] = [
  { title: 'What can you help me with?' },
  { title: 'Give me a few examples of tasks you can do.' },
  { title: 'Help me research how to approach a specific challenge, issue or topic.' },
];

const REPORTING_PATHS = [
  '/surveys/overview',
  '/surveys/response-rates',
  '/surveys/results',
  '/surveys/analyze',
  '/surveys/heatmap',
];

const isReportingPage = (location: Location<any>) => REPORTING_PATHS.some((path) => location.pathname.startsWith(path));

const reportingPagePrompts: SuggestedPrompt[] = [
  {
    title:
      'Tell me what employees are saying about a specific topic, combining insight from qualitative and quantitative data, on this survey.',
    match: isReportingPage,
  },
  {
    title: 'Tell me what employees with a specific attribute are saying about a specific topic on this survey.',
    match: isReportingPage,
  },
  {
    title: 'Summarize the results of this survey.',
    match: isReportingPage,
  },
  {
    title: 'Summarize the results of this survey by a specific attribute (list attributes).',
    match: isReportingPage,
  },
  {
    title: 'Summarize comments related to a specific topic, on this survey.',
    match: isReportingPage,
  },
  {
    title: 'Summarize comments related to a specific topic for a specific attribute, on this survey.',
    match: isReportingPage,
  },
  {
    title: 'Recommended actions based on the results from this survey.',
    match: isReportingPage,
  },
  {
    title: "What action can I take to improve my team's lowest score?",
    match: isReportingPage,
  },
  {
    title:
      'Create an action item; first recommend actions from this survey and let me choose which one should be created.',
    match: isReportingPage,
  },
  {
    title: 'What factors dropped from last survey?',
    match: isReportingPage,
  },
  {
    title: 'How does this survey trend against other surveys?',
    match: isReportingPage,
  },
  {
    title: 'Show how a factor is trending on a graph between this survey and others.',
    match: isReportingPage,
  },
  {
    title: 'Identify attributes with the largest variation in scores.',
    match: isReportingPage,
  },
  {
    title: 'Identify employee groups with the lowest scores, on this survey. (This will take time)',
    match: isReportingPage,
  },
  {
    title:
      'Compare scores and comments from employees with attribute value x against employees with attribute value y.',
    match: isReportingPage,
  },
];

const isSurveyListPage = (location: Location<any>) => location.pathname === '/surveys';

const surveyListPagePrompts: SuggestedPrompt[] = [
  {
    title:
      'Tell me what employees with a specific attribute value are saying about a specific topic, from a specific survey.',
    match: isSurveyListPage,
  },
  {
    title: 'Recommend actions based on the results of the last survey.',
    match: isSurveyListPage,
  },
  {
    title: 'Analyze the trend between specific surveys.',
    match: isSurveyListPage,
  },
  {
    title: 'Show how a factor is trending on a graph between surveys.',
    match: isSurveyListPage,
  },
  {
    title: 'Identify groups with lowest scores, on a specific survey.',
    match: isSurveyListPage,
  },
];

const actionPlanningPagePrompts: SuggestedPrompt[] = [
  {
    title: 'Create an Action Item.',
    match: (location) => location.pathname === '/action-planning/actions',
  },
];

const suggestedPagePrompts: SuggestedPrompt[] = [
  ...reportingPagePrompts,
  ...surveyListPagePrompts,
  ...actionPlanningPagePrompts,
];
